// Import Packages
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState, useRecoilState } from 'recoil';

// Import Material UI
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import ErrorSharpIcon from '@material-ui/icons/ErrorSharp';
import { Button, Dialog, DialogTitle } from '@material-ui/core';
import DialogContent from "@material-ui/core/DialogContent";
import Modal from '@material-ui/core/Modal';
import Slide from '@material-ui/core/Slide';
import Close from "@material-ui/icons/Close";
import DialogActions from '@material-ui/core/DialogActions';
import Divider from '@material-ui/core/Divider';

// Import Pages
import { alertState, triggerConf, triggerSelection } from '../../../../../../recoil-store/recoil-state';
import TriggerStatic
    from '../../../../../../components/FlowConfigurationForms/TriggerStatic/TriggerStatic/TriggerStatic';
import { libraryIcons } from '../../../autom/NewFlowLibraryAssetManager';
import Info from "../../../autom/editAction/components/Info";

// Import Helpers
import { capitalizeFirstLetter, conditionalOperators } from "../../../../../../helpers/helper";

// Import Services
import automService from '../../../../../../services/AutomService/AutomService'

// Import CSS
import '../MiddleArea.css';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const TriggerModal = ({
    automId,
    automVersionId,
    openTriggerConfigure,
    setOpenTriggerConfigure,
    triggerOffSet,
    variables,
    setOpenVariable,
    automDetail,
    setAutomDetail,
    reCheck,
    setReCheck
}) => {
    const { t } = useTranslation();
    const setAlert = useSetRecoilState(alertState);
    const refModal = useRef(null);

    const [offSet, setOffSet] = useState(null);
    const [conf, setConf] = useRecoilState(triggerConf);
    const [triggerSelectionData, setTriggerSelectionData] = useRecoilState(triggerSelection);
    const [triggerConfig, setTriggerConfig] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [isValid, setIsValid] = useState(false);

    const triggerStaticTriggerLabel = "CONTENT.CONFIGURATION-FORM.TRIGGER-STATIC.TRIGGERS."

    useEffect(() => {
        if (openTriggerConfigure) {
            setOffSet({ left: triggerOffSet.left, top: triggerOffSet.top, step: 1 })
            setTriggerConfig(JSON.parse(JSON.stringify(conf)));
            setTimeout(() => {
                let left = 20;
                let step = 0;
                let valid = false;
                if (Object.keys(conf.app).length > 0) {
                    valid = true;
                    if (Object.keys(conf.trigger).length > 0) {
                        step = 1;
                        if (Object.keys(conf.connector).length > 0) {
                            step = 2;
                            if (Object.keys(conf.setup).length > 0) {
                                left = 10;
                                step = 3;
                            }
                        }
                    }
                }
                setIsValid(valid);
                setActiveStep(step);
                setOffSet({ left, top: getTopPx(), step: 2 })
            }, 1000)
        }
    }, [openTriggerConfigure])

    useEffect(() => {
        if (refModal && refModal.current) {

            const top = getTopPx();
            if (offSet.top !== top) {
                setOffSet(item => ({
                    ...item,
                    top
                }))
            }
        }
    }, [triggerConfig])

    const closeModal = () => {
        setOffSet({ left: triggerOffSet.left, top: triggerOffSet.top, step: 1 })
        setTimeout(() => {
            setOpenTriggerConfigure(false);
        }, 1000)
    }

    const getTopPx = () => {
        let parent = refModal.current.offsetParent;
        while (parent.offsetParent) {
            parent = parent.offsetParent;
        }
        return parent.offsetHeight / 2 - refModal.current.offsetHeight / 2;
    }

    const backStep = () => {
        setActiveStep(activeStep - 1);
        if (activeStep - 1 === 2 && offSet.left !== 20) {
            setOffSet({ left: 20, top: getTopPx(), step: 2 })
        }
        if (!isValid) {
            setIsValid(true);
        }
    }

    const nextStep = () => {
        if (isValid) {
            switch (activeStep + 1) {
                case 1:
                    if (!triggerConfig?.trigger?.id) {
                        setIsValid(false);
                    }
                    break;
                case 2:
                    if (!triggerConfig?.connector?.id) {
                        setIsValid(false);
                    }
                    break;
                case 3:
                    if (offSet.left === 20) {
                        setOffSet({ left: 10, top: getTopPx(), step: 2 })
                    }
                    break;
                default:
                    break;
            }
            setActiveStep(activeStep + 1);
        }
    }

    const saveTrigger = async () => {
        setConf(JSON.parse(JSON.stringify(triggerConfig)));
        const response = await automService.updateTrigger({
            version_id: automVersionId,
            trigger_type: triggerConfig.trigger.type,
            trigger_config: {
                trigger_mode_id: triggerConfig.trigger.id,
                application_credential_id: triggerConfig.connector.id,
                trigger_setup: triggerConfig.setup
            },
            mode: 'app'
        }, automId);

        if (response && response.status) {
            const type = triggerConfig.trigger.type === "realtime" ? "webhook,app" : triggerConfig.trigger.type === "schedule" ? triggerConfig.trigger.type + ",app" : null;
            setTriggerSelectionData(type.split(','));
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t("MESSAGES.TRIGGER." + response.message),
                severity: response.status ? 'success' : 'error'
            }))
            setAutomDetail({
                ...automDetail,
                integration_type: triggerConfig.trigger.type === "realtime" ? "TRIGGER" : triggerConfig.trigger.type === "schedule" ? "SCHEDULE" : "STANDARD",
                trigger_type: type,
                trigger_mode_id: triggerConfig.trigger.id
            });
            setReCheck(!reCheck);
            closeModal();
        }
    }

    return (
        offSet && <>
            <Modal
                open={openTriggerConfigure}
            >
                <div ref={refModal} className={`new-flow-item-line trigger-modal-container`} style={{
                    left: `${offSet.left}${offSet.step === 2 ? '%' : 'px'}`,
                    top: `${offSet.step === 1 ? triggerOffSet.top : offSet.top}px`
                }}
                >
                    <div className={`option-container`}
                        style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <WarningRoundedIcon className="new-flow-warning-icon" /> {/* id={'flow-warning' + item.id} */}
                        <Info value={t("CONTENT.FLOW.SHOW-ERROR")} placement="left">
                            <ErrorSharpIcon
                                className={`debug-error-icon`}
                                id={'debug-error'} />
                        </Info>
                        <div
                            className={`action-icon-border action-selected border-transparent`}>
                            <div
                                id={'flow-regular'}
                                className={`new-flow-standard-item trigger-app-icon ${triggerConfig && Object.keys(triggerConfig.app).length > 0 ? 'color-white' : 'color-blue'} color-trigger_static`}>
                                {/* {libraryIcons["trigger_static"]} */}
                                {
                                    triggerConfig && Object.keys(triggerConfig.app).length > 0 ? (

                                        <img src={triggerConfig.app?.image_path} alt={triggerConfig.app?.name}
                                            className="selected-trigger-app" />


                                    ) : libraryIcons["trigger_static"]
                                }

                            </div>
                        </div>

                    </div>
                    {

                        <div className="new-flow-row">
                            <div className="trigger-panel">
                                <h4>{t(triggerStaticTriggerLabel + 'TRIGGER')}</h4>
                                {triggerConfig && Object.keys(triggerConfig.trigger).length > 0 ? (
                                    <div className='trigger-item-container'>
                                        <div
                                            className="trigger-pane">{t(triggerStaticTriggerLabel + triggerConfig.trigger?.name)} ({capitalizeFirstLetter(triggerConfig.trigger?.type)})
                                        </div>
                                        {triggerConfig.setup.conditions?.length > 0 && triggerConfig.setup.conditions[0].condition !== "" && (
                                            <div className="add-trigger-setup-block">
                                                {triggerConfig.setup.conditions.map((item, i) => {
                                                    let prevItem;
                                                    if (i - 1 >= 0) prevItem = triggerConfig.setup.conditions[i - 1];
                                                    return (
                                                        <>
                                                            {item.source.length > 0 && item.condition !== "" && (
                                                                <>
                                                                    {prevItem && prevItem.op_logic && prevItem.source.length > 0 && prevItem.condition !== "" && (
                                                                        <div className="op-logic-area">
                                                                            {prevItem.op_logic.toUpperCase()}
                                                                        </div>
                                                                    )}
                                                                    <div key={i} className="condition-block">
                                                                        <div className="source-area">
                                                                            <span className="if-block">
                                                                                {`# ${i + 1} ` + t("CONTENT.FLOW.CONDITION").toUpperCase()}
                                                                            </span>
                                                                            <span className="if-selection source-datas">
                                                                                <div
                                                                                    className='condition-source-container'>
                                                                                    {
                                                                                        item.source?.map((v, i) => {
                                                                                            return <div
                                                                                                className="source-param">{v.text}</div>
                                                                                        })
                                                                                    }
                                                                                </div>

                                                                                <div
                                                                                    className="condition-param">{conditionalOperators[item.condition]?.text}</div>
                                                                            </span>
                                                                            {item.condition !== "e" && item.condition !== "ne" && item.target !== "" && (
                                                                                <>
                                                                                    <Divider
                                                                                        style={{ backgroundColor: '#e6e6e6' }} />

                                                                                    <span className="if-selection">
                                                                                        {/* <div className="target-area">{item.target}</div> */}
                                                                                        <div
                                                                                            className="source-param">{item.target}</div>
                                                                                    </span>
                                                                                </>
                                                                            )}
                                                                        </div>

                                                                    </div>
                                                                </>
                                                            )}
                                                        </>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <Button disabled='false' className={`step-${offSet.step}-configure-button`}
                                            variant="contained">
                                            {t(triggerStaticTriggerLabel + (offSet.step === 2 ? (triggerConfig?.app?.id ? 'SELECT-TRIGGER' : 'YOUR-CONFIG-HERE') : 'CONFIGURE-TRIGGER'))}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>

                    }
                </div>

            </Modal>

            <Dialog
                open={openTriggerConfigure && offSet.step > 1}
                TransitionComponent={Transition}
                disableEnforceFocus
                PaperProps={{
                    style: {
                        width: '30%',
                        height: '87%',
                        position: 'absolute',
                        left: `${offSet.left === 10 ? '60' : '50'}%`,
                        transition: 'left 0.5s'
                    },
                }}
            >
                <DialogTitle id="alert-dialog-title" style={{ padding: '30px 30px 20px' }}>
                    <div id="about-title" style={{ fontSize: '16px' }}>
                        Trigger Configuration
                        <Button data-test="cancelautom-button" onClick={closeModal}>
                            <Close />
                        </Button>

                    </div>
                    <Divider style={{ margin: '12px 20px 0px 2px', backgroundColor: '#f4f2f2' }} />
                </DialogTitle>
                <DialogContent style={{ padding: '0px', overflowY: 'hidden' }}>
                    <TriggerStatic variables={variables} setOpenVariable={setOpenVariable}
                        triggerConfig={triggerConfig} setTriggerConf={setTriggerConfig}
                        isValid={isValid} setIsValid={setIsValid} automVersionId={automVersionId}
                        activeStep={activeStep} setActiveStep={setActiveStep} automId={automId}
                    />
                </DialogContent>

                <DialogActions style={{ justifyContent: 'space-between' }}>
                    <Button
                        className={`trigger-step-button ${activeStep === 0 ? `pasif` : 'back-active'}`}
                        disabled={activeStep === 0}
                        onClick={backStep}
                        variant="outlined"
                    >
                        {t('CONTENT.MODAL.BACK')}
                    </Button>
                    <Button
                        disabled={!isValid}
                        className={`trigger-step-button ${isValid ? 'active' : 'pasif'}`}
                        onClick={() => activeStep !== 3 ? nextStep() : saveTrigger()}
                        variant="outlined"
                    >
                        {t('CONTENT.MODAL.' + (activeStep !== 3 ? 'NEXT' : 'SAVE'))}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default TriggerModal;
